import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserFilterRequest,
  UpdateUserRolesRequest,
  ChangePasswordRequest,
  UpdateUserOrganizationRequest,
  OpenTicketHandoff
} from '../types/users';

// Get users list with optional filters
// organizationId is optional for multi-org support (when managing users in Org Settings)
export function useUsers(filters?: UserFilterRequest, organizationId?: string) {
  return useQuery({
    // Include organizationId in query key for proper cache isolation between organizations
    queryKey: organizationId
      ? ['users', 'list', organizationId, filters]
      : (filters ? queryKeys.users.list(filters) : queryKeys.users.lists()),
    queryFn: async () => {
      const response = await apiService.getUsers(filters, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch users'));
      }
      // Backend returns UserListResponse
      return response.data || { users: [], totalCount: 0, currentPage: 1, pageSize: 20, pageCount: 0 };
    },
    placeholderData: keepPreviousData,
  });
}

// Get single user by ID
export function useUser(userId: string) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: async () => {
      const response = await apiService.getUser(userId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch user'));
      }
      return response.data;
    },
    enabled: !!userId,
  });
}

// Get user statistics
// organizationId is optional for multi-org support (when managing users in Org Settings)
export function useUserStats(organizationId?: string) {
  return useQuery({
    // Include organizationId in query key for proper cache isolation between organizations
    queryKey: organizationId ? ['users', 'stats', organizationId] : ['users', 'stats'],
    queryFn: async () => {
      const response = await apiService.getUserStats(organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch user stats'));
      }
      return response.data;
    },
  });
}

// Get user roles
export function useUserRoles(userId: string) {
  return useQuery({
    queryKey: ['users', userId, 'roles'],
    queryFn: async () => {
      const response = await apiService.getUserRoles(userId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch user roles'));
      }
      return response.data;
    },
    enabled: !!userId,
  });
}

// Get available roles
export function useAvailableRoles() {
  return useQuery({
    queryKey: ['users', 'available-roles'],
    queryFn: async () => {
      const response = await apiService.getAvailableRoles();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch available roles'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - roles don't change often
  });
}

// Hook for getting users who can be assigned to tickets
// organizationId is required for multi-org architecture
export function useAssignableUsers(organizationId?: string) {
  return useQuery({
    queryKey: organizationId ? ['users', 'assignable', organizationId] : ['users', 'assignable'],
    queryFn: async () => {
      const response = await apiService.getUsersForAssignment(organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch assignable users'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId, // Don't fetch until organizationId is set
  });
}

// Create user mutation
// organizationId is optional for multi-org support (when managing users in Org Settings)
export function useCreateUser(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: CreateUserRequest) => {
      const response = await apiService.createUser(user, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to create user'));
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate users list and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
      // Also invalidate org-specific queries if organizationId was provided
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: ['users', 'list', organizationId] });
        queryClient.invalidateQueries({ queryKey: ['users', 'stats', organizationId] });
      }
    },
  });
}

// Update user mutation
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: UpdateUserRequest 
    }) => {
      const response = await apiService.updateUser(id, updates);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update user'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate specific user and list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
    },
  });
}

// Delete user mutation
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiService.deleteUser(userId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete user'));
      }
      return response.data;
    },
    onSuccess: (_, userId) => {
      // Invalidate users list and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
      // Remove the specific user from cache
      queryClient.removeQueries({ queryKey: queryKeys.users.detail(userId) });
    },
  });
}

// Update user roles mutation
export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      roles 
    }: { 
      userId: string; 
      roles: UpdateUserRolesRequest 
    }) => {
      const response = await apiService.updateUserRoles(userId, roles);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update user roles'));
      }
      return response.data;
    },
    onSuccess: (_, { userId }) => {
      // Invalidate specific user, roles, and lists
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'roles'] });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
    },
  });
}

// Change user password mutation
export function useChangeUserPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      passwordData 
    }: { 
      userId: string; 
      passwordData: ChangePasswordRequest 
    }) => {
      const response = await apiService.changeUserPassword(userId, passwordData);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to change user password'));
      }
      return response.data;
    },
    onSuccess: (_, { userId }) => {
      // Invalidate specific user details
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
    },
  });
}

// Tickets a member is assigned to that aren't closed - deactivating or removing them must hand these off
export function useMemberOpenTicketCount(userId: string | undefined, organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.users.openTicketCount(userId ?? '', organizationId),
    queryFn: async () => {
      const response = await apiService.getMemberOpenTicketCount(userId!, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to count their open tickets'));
      }
      return response.data ?? 0;
    },
    enabled: enabled && !!userId,
    staleTime: 0,
  });
}

// Update user organization relationship mutation
// organizationId is optional for multi-org support (when managing users in Org Settings)
export function useUpdateUserOrganization(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      orgData
    }: {
      userId: string;
      orgData: UpdateUserOrganizationRequest
    }) => {
      const response = await apiService.updateUserOrganization(userId, orgData, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update user organization relationship'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate specific user and list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(data.userId) });
      // Deactivating hands off the member's tickets
      if (data.handedOffTicketCount > 0) {
        queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
      }
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
      // Also invalidate org-specific queries if organizationId was provided
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: ['users', 'list', organizationId] });
        queryClient.invalidateQueries({ queryKey: ['users', 'stats', organizationId] });
      }
    },
  });
}

// Remove user from organization mutation
// organizationId is optional for multi-org support (when managing users in Org Settings)
export function useRemoveUserFromOrganization(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, handoff }: { userId: string; handoff?: OpenTicketHandoff }) => {
      const response = await apiService.removeUserFromOrganization(userId, organizationId, handoff);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to remove user from organization'));
      }
      return response.data;
    },
    onSuccess: (_, { userId }) => {
      // Removing hands off the member's tickets and drops their follows
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
      // Invalidate users list and stats, remove specific user from cache
      queryClient.invalidateQueries({ queryKey: invalidateQueries.users.lists() });
      queryClient.invalidateQueries({ queryKey: ['users', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignment'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'assignable'] });
      // Remove the specific user from cache
      queryClient.removeQueries({ queryKey: queryKeys.users.detail(userId) });
      // Also invalidate org-specific queries if organizationId was provided
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: ['users', 'list', organizationId] });
        queryClient.invalidateQueries({ queryKey: ['users', 'stats', organizationId] });
      }
    },
  });
}